/**
 * aiChatService — single client entry point for ALL AI prompts and conversations.
 *
 * Every request goes to the mieGPT endpoint (https://chat.mieros.cloud/gptj, configurable via
 * VITE_LOCAL_LLM_URL) with a plain { prompt } body and a { generated_text } response.
 * There is no OpenAI fallback and no provider switching.
 *
 * Agent chat with RAG/profile/FHIR context uses callAgentChat(), which calls the MiEMarketApi
 * /api/ai/chat route; that route also forwards to the same endpoint.
 */

import axios from 'axios';
import { getAuth } from 'firebase/auth';
import config from '../utils/EnvironmentSwitcher';

const AI_PROXY_ENDPOINT = `${config.apiUrl}/api/ai/chat`;

const getIdToken = async () => {
  const auth = getAuth();
  return auth.currentUser ? await auth.currentUser.getIdToken() : null;
};

// Combine a system message and user prompt into the single prompt string the
// local GPT-J endpoint expects (it has no messages[] contract).
const buildPrompt = (systemMessage, userPrompt) =>
  [systemMessage, userPrompt].filter(Boolean).join('\n\n');

// Defensive parse of the local endpoint response. The reference implementation
// in GptMiE.js reads `generated_text`; we also accept a few common shapes.
const extractLocalText = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data.trim();
  return (
    data.generated_text ||
    data.response ||
    data.text ||
    data.content ||
    ''
  ).toString().trim();
};

const callLocalLlm = async (prompt) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), config.localLlmTimeoutMs);
  try {
    const { data } = await axios.post(
      config.localLlmUrl,
      { prompt },
      {
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        timeout: config.localLlmTimeoutMs,
      }
    );
    const text = extractLocalText(data);
    if (!text) throw new Error('Local LLM returned empty response');
    return text;
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * callAgentChat — agent Test Chat via MiEMarketApi.
 *
 * Always goes through POST /api/ai/chat with agentId so the server:
 *   1. Injects agent system prompt, knowledge RAG, profile, FHIR context
 *   2. Sends the result to https://chat.mieros.cloud/gptj (mieGPT)
 *
 * @param {object} params
 * @param {Array<{role:string,content:string}>} params.messages Full chat messages (incl. system).
 * @param {string} params.agentId Agent UUID for RAG / prompt injection.
 * @param {string} [params.model]
 * @param {number} [params.maxTokens]
 * @param {number} [params.temperature]
 * @returns {Promise<{ content: string, provider?: string, raw: object }>}
 */
export const callAgentChat = async ({
  messages,
  agentId,
  model = 'gpt-4o-mini',
  maxTokens = 1200,
  temperature = 0.7,
} = {}) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new Error('messages array is required');
  }
  if (!agentId) {
    throw new Error('agentId is required');
  }

  const idToken = await getIdToken();
  const { data } = await axios.post(
    AI_PROXY_ENDPOINT,
    {
      model,
      messages,
      max_tokens: maxTokens,
      temperature,
      agentId,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      // Local LLM can be slow (ollama preload); allow a generous client timeout.
      timeout: Math.max(config.localLlmTimeoutMs || 180000, 60000) + 15000,
    }
  );

  const content =
    data?.choices?.[0]?.message?.content ||
    data?.reply ||
    data?.output ||
    (typeof data === 'string' ? data : '') ||
    '';

  if (!content) {
    throw new Error(data?.error || 'No response from AI chat');
  }

  return {
    content,
    provider: data?.provider || null,
    raw: data,
  };
};

// Flatten an OpenAI-style messages[] history into the single prompt string the endpoint expects.
const messagesToPrompt = (messages) =>
  (messages || [])
    .filter((m) => m && typeof m.content === 'string' && m.content.trim())
    .map((m) => {
      const role = (m.role || 'user').toLowerCase();
      if (role === 'system') return m.content.trim();
      if (role === 'assistant') return `Assistant: ${m.content.trim()}`;
      return `User: ${m.content.trim()}`;
    })
    .join('\n\n');

/**
 * callAI — send a prompt to the mieGPT endpoint.
 *
 * @param {object} params
 * @param {string} [params.prompt]        The user prompt (required unless `messages` is given).
 * @param {string} [params.systemMessage] Optional system message.
 * @param {Array<{role:string,content:string}>} [params.messages] Conversation history (multi-turn).
 * @returns {Promise<string>} The assistant response text.
 */
export const callAI = async ({ prompt, systemMessage, messages } = {}) => {
  const fullPrompt = Array.isArray(messages) && messages.length
    ? messagesToPrompt(systemMessage ? [{ role: 'system', content: systemMessage }, ...messages] : messages)
    : buildPrompt(systemMessage, prompt || '');
  if (!fullPrompt.trim()) throw new Error('prompt is required');
  return callLocalLlm(fullPrompt);
};

export default callAI;
